// ⚠️ GENERATED COPY — DO NOT EDIT.
// Source: shared/ui/survey/widgets/ProviderSearchWidget.tsx — edit there, then run: bash scripts/sync_shared_code.sh
// CI verifies these copies match the source (sync_shared_code.sh --check).
import React, { useState, useEffect, useRef } from "react";

interface ProviderResult {
  id?: string;
  name: string;
  specialty?: string;
  location?: string;
  phone?: string;
  fax?: string;
  licenceNumber?: string;
  zone?: string;
  [key: string]: unknown;
}

interface ProviderSelection {
  id?: string;
  firstName: string;
  lastName: string;
  displayName: string;
  specialty: string;
  phone: string;
  fax: string;
  city: string;
  licenceNumber: string;
  zone: string;
}

interface ProviderSearchWidgetProps {
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (provider: ProviderSelection) => void;
  mode?: string;
}

/**
 * Provider (physician) search autocomplete widget.
 * Calls /api/physician/directory to find physicians by name/location.
 * When a provider is selected, populates physician fields.
 */
export default function ProviderSearchWidget({ value, onChange, onSelect, mode = "patient" }: ProviderSearchWidgetProps) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState<ProviderResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ProviderResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchProviders = async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/physician/directory?search=${encodeURIComponent(searchQuery)}&limit=8`);
      if (res.ok) {
        const data = await res.json();
        setResults(data.results || []);
        setShowDropdown(true);
      }
    } catch (err) {
      console.warn("Provider search failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setSelectedProvider(null);
    if (onChange) onChange(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchProviders(val), 300);
  };

  const formatName = (rawName: string) => {
    // Directory format: "LastName  FirstName M." → "Dr. FirstName LastName"
    const parts = rawName.trim().split(/\s{2,}|\s*,\s*/);
    if (parts.length >= 2) {
      const lastName = parts[0].trim();
      const firstName = parts[1].trim();
      return `Dr. ${firstName} ${lastName}`;
    }
    return `Dr. ${rawName}`;
  };

  const handleSelect = (provider: ProviderResult) => {
    setSelectedProvider(provider);
    const displayName = formatName(provider.name);
    setQuery(displayName);
    setShowDropdown(false);

    if (onChange) onChange(displayName);

    // Pass full provider details back for batch fill
    if (onSelect) {
      const nameParts = provider.name.trim().split(/\s{2,}|\s*,\s*/);
      const lastName = nameParts[0]?.trim() || "";
      const firstName = nameParts[1]?.trim() || "";
      onSelect({
        id: provider.id,
        firstName,
        lastName,
        displayName,
        specialty: provider.specialty || "",
        phone: provider.phone || "",
        fax: provider.fax || "",
        city: provider.location || "",
        licenceNumber: provider.licenceNumber || "",
        zone: provider.zone || "",
      });
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: mode === "patient" ? "14px 16px" : "10px 12px",
    fontSize: mode === "patient" ? 18 : 14,
    border: "2px solid #d8c9b6",
    borderRadius: 8,
    outline: "none",
    fontFamily: "system-ui, -apple-system, sans-serif",
    color: "#182a33",
    boxSizing: "border-box",
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={() => results.length > 0 && setShowDropdown(true)}
        placeholder="Start typing your doctor's name..."
        style={inputStyle}
      />
      {loading && (
        <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#5b6e7c", fontSize: 13 }}>
          Searching...
        </div>
      )}

      {showDropdown && results.length > 0 && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          background: "white",
          border: "2px solid #d8c9b6",
          borderTop: "none",
          borderRadius: "0 0 8px 8px",
          maxHeight: 320,
          overflowY: "auto",
          zIndex: 100,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}>
          {results.map((provider, i) => (
            <div
              key={provider.id || i}
              onClick={() => handleSelect(provider)}
              style={{
                padding: "12px 16px",
                cursor: "pointer",
                borderBottom: i < results.length - 1 ? "1px solid #ede6dc" : "none",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f5f0eb"}
              onMouseLeave={(e) => e.currentTarget.style.background = "white"}
            >
              <div style={{ fontWeight: 600, color: "#182a33", fontSize: mode === "patient" ? 16 : 14 }}>
                {formatName(provider.name)}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 3, flexWrap: "wrap" }}>
                {provider.specialty && (
                  <span style={{
                    padding: "2px 8px", borderRadius: 10, fontSize: 12,
                    backgroundColor: "#e4ecee", color: "#182a33",
                  }}>
                    {provider.specialty}
                  </span>
                )}
                {provider.location && (
                  <span style={{
                    padding: "2px 8px", borderRadius: 10, fontSize: 12,
                    backgroundColor: "#f4eef4", color: "#734a76",
                  }}>
                    {provider.location}
                  </span>
                )}
              </div>
              {provider.phone && (
                <div style={{ color: "#5b6e7c", fontSize: mode === "patient" ? 13 : 11, marginTop: 3 }}>
                  {provider.phone}
                  {provider.fax && ` | Fax: ${provider.fax}`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showDropdown && results.length === 0 && query.length >= 2 && !loading && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          background: "white",
          border: "2px solid #d8c9b6",
          borderTop: "none",
          borderRadius: "0 0 8px 8px",
          padding: "16px",
          color: "#5b6e7c",
          fontSize: 14,
          zIndex: 100,
        }}>
          No providers found. You can type the name manually below.
        </div>
      )}

      {selectedProvider && (
        <div style={{
          marginTop: 12,
          padding: "12px 16px",
          background: "#eef4f2",
          borderRadius: 8,
          border: "1px solid #bbd5cc",
        }}>
          <div style={{ fontWeight: 600, color: "#6b9c8e", fontSize: 15, marginBottom: 4 }}>
            {formatName(selectedProvider.name)}
          </div>
          <div style={{ color: "#555", fontSize: 13 }}>
            {selectedProvider.specialty}
            {selectedProvider.location && ` — ${selectedProvider.location}`}
          </div>
          {selectedProvider.phone && (
            <div style={{ color: "#555", fontSize: 13, marginTop: 2 }}>
              Phone: {selectedProvider.phone}
              {selectedProvider.fax && ` | Fax: ${selectedProvider.fax}`}
            </div>
          )}
          <div style={{ fontSize: 12, color: "#6b9c8e", marginTop: 6, fontStyle: "italic" }}>
            Provider details will be saved to your profile
          </div>
        </div>
      )}
    </div>
  );
}
